export interface ServiceSpec {
  label: string;
  value: string;
}

export interface ServiceFeature {
  icon: string;
  title: string;
  description: string;
}

export interface ProductVariant {
  id: string;
  name: string;
  description?: string;
  specifications: ServiceSpec[];
  gallery?: string[];
  galleryAlt?: string[];
}

export interface ServiceData {
  title: string;
  subtitle: string;
  description: string;
  mainImage: string;
  mainImageAlt?: string;
  bannerImages?: string[];
  bannerImagesAlt?: string[];
  features: ServiceFeature[];
  specifications: ServiceSpec[];
  pricing?: {
    basePrice: string;
    priceDescription: string;
    additionalInfo: string;
  };
  gallery?: string[];
  galleryAlt?: string[];
  benefits: string[];
  applications: string[];
  // New: Support for product variants
  hasVariants?: boolean;
  variants?: ProductVariant[];
}

export const servicesData: Record<string, ServiceData> = {
  'banos-portatiles-vip': {
    title: 'Baño Portátil VIP',
    subtitle: 'Máximo confort y elegancia para eventos exclusivos',
    description: 'Lavamanos interno, asiento con tapa, piso antideslizante, urinario de pared, gancho para abrigo o herramientas.',
    mainImage: '/images/services/bano-portatil-vip.png',
    mainImageAlt: 'Baño portátil VIP de Junisama con lavamanos interno y acabados premium para eventos exclusivos en Colombia',
    bannerImages: [
      '/images/services/bano-portatil-vip.png'
    ],
    bannerImagesAlt: [
      'Vista exterior del baño portátil VIP Junisama con acabados de alta calidad'
    ],
    features: [
      {
        icon: '🚿',
        title: 'Lavamanos Interno',
        description: 'Lavamanos integrado con dispensador de jabón'
      },
      {
        icon: '🚽',
        title: 'Asiento con Tapa',
        description: 'Inodoro completo con asiento y tapa de calidad'
      },
      {
        icon: '👟',
        title: 'Piso Antideslizante',
        description: 'Superficie segura y antideslizante'
      },
      {
        icon: '🚿',
        title: 'Urinario de Pared',
        description: 'Urinario masculino integrado en la pared'
      },
      {
        icon: '🧥',
        title: 'Gancho para Abrigo',
        description: 'Gancho para colgar ropa o herramientas'
      },
      {
        icon: '🏗️',
        title: 'Diseño Robusto',
        description: 'Construcción resistente para uso intensivo'
      }
    ],
    specifications: [
      { label: 'Altura (interno y externo)', value: '2.12 M - 2.33 M' },
      { label: 'Peso Neto', value: '76 kilogramos' },
      { label: 'Número de usos', value: '220' },
      { label: 'Capacidad', value: '210 litros' },
      { label: 'Ancho (interno y externo)', value: '1 M - 1.06 M' },
      { label: 'Material', value: 'Polietileno rotomoldeado' },
      { label: 'Color', value: 'Naranja y gris' },
      { label: 'Ventilación', value: 'Natural optimizada' }
    ],
    pricing: {
      basePrice: 'De 1 a 5 unidades: $200.000 COP por unidad\nDe 6 a 10 unidades: $180.000 COP por unidad\nMás de 10 unidades: $150.000 COP por unidad',
      priceDescription: '',
      additionalInfo: 'Precios por día. Incluye instalación, mantenimiento diario y recolección. Descuentos por alquiler semanal y mensual.'
    },
    gallery: [],
    benefits: [
      'Experiencia premium para invitados VIP',
      'Imagen corporativa de alto nivel',
      'Máximo confort y privacidad',
      'Instalación y mantenimiento incluido',
      'Disponible 24/7 durante el evento',
      'Personal especializado en eventos exclusivos'
    ],
    applications: [
      'Bodas de lujo y eventos sociales',
      'Eventos corporativos ejecutivos',
      'Lanzamientos de productos premium',
      'Ferias comerciales de alto nivel',
      'Festivales VIP y conciertos',
      'Eventos deportivos exclusivos'
    ]
  },

  'banos-portatiles-estandar': {
    title: 'Baños Portátiles Estándar',
    subtitle: 'Sanitario portátil de alta resistencia, diseñado para optimizar la higiene y minimizar el mantenimiento',
    description: 'Ideal para entornos exigentes como obras, eventos o zonas rurales. Disponible en varios colores para una fácil identificación en campo.',
    mainImage: '/images/banos-estandar-main.jpg',
    mainImageAlt: 'Baño portátil estándar Junisama de alta resistencia para obras, eventos y zonas rurales en Colombia',
    bannerImages: [
      '/images/services/bano-estandar.webp',
      '/images/services/baño-estandar-2.webp'
    ],
    bannerImagesAlt: [
      'Vista exterior del baño portátil estándar Junisama',
      'Interior del baño portátil estándar mostrando inodoro y ventilación'
    ],
    features: [
      {
        icon: '🚽',
        title: 'Inodoro de Porcelana',
        description: 'Inodoro de alta calidad con sistema de descarga eficiente'
      },
      {
        icon: '🚿',
        title: 'Lavamanos Integrado',
        description: 'Lavamanos con dispensador de jabón y toallas'
      },
      {
        icon: '🌬️',
        title: 'Ventilación Natural',
        description: 'Sistema de ventilación optimizado para confort'
      },
      {
        icon: '🔐',
        title: 'Cerradura Segura',
        description: 'Sistema de bloqueo interno confiable'
      },
      {
        icon: '🧻',
        title: 'Papel y Accesorios',
        description: 'Papel higiénico, jabón y dispensadores incluidos'
      },
      {
        icon: '♻️',
        title: 'Eco-Friendly',
        description: 'Productos biodegradables y respetuosos con el ambiente'
      }
    ],
    specifications: [
      { label: 'Peso (Vacío)', value: '83,91 Kg (185 lbs)' },
      { label: 'Capacidad del depósito', value: '227 Litros (60 gal US)' },
      { label: 'Ancho', value: '109,2 cm (43")' },
      { label: 'Altura', value: '220 cm (86,6")' },
      { label: 'Profundidad', value: '119,8 cm (47,20")' }
    ],
    pricing: {
      basePrice: '$45.000',
      priceDescription: 'por día',
      additionalInfo: 'Incluye instalación y recolección. Mantenimiento cada 3 días. Tarifas especiales para alquiler prolongado.'
    },
    gallery: [
      '/images/gallery/galeria estandar1_mgzsv4zoye53d.webp',
      '/images/gallery/galeria estandar 2_mgzsv508bwdhy.webp',
      '/images/gallery/galeria estandar 3_mgzsuoz14c4zu.webp',
      '/images/gallery/galeria estandar 4_mgzsv4zlfuu4h.webp'
    ],
    benefits: [
      'Solución económica y eficiente',
      'Instalación rápida y sencilla',
      'Mantenimiento profesional incluido',
      'Productos biodegradables',
      'Disponibilidad inmediata',
      'Ideal para eventos masivos'
    ],
    applications: [
      'Festivales y conciertos',
      'Obras de construcción',
      'Eventos deportivos',
      'Ferias y exposiciones',
      'Eventos comunitarios',
      'Actividades al aire libre'
    ]
  },

  'lavamanos-aquastand-aquapop': {
    title: 'Lavamanos Aquastand y Aquapop',
    subtitle: 'Estaciones de higiene portátiles de última generación',
    description: 'Sistemas de lavado de manos autónomos con tecnología sin contacto, perfectos para complementar nuestros servicios sanitarios y garantizar la higiene en cualquier evento.',
    mainImage: '/images/lavamanos-main.jpg',
    mainImageAlt: 'Lavamanos portátiles Aquastand y Aquapop de Junisama con tecnología sin contacto para eventos en Colombia',
    bannerImages: [
      '/images/services/Aquastand.webp',
      '/images/services/Aquapop.webp',
      '/images/services/Aquapop-2.webp'
    ],
    bannerImagesAlt: [
      'Lavamanos Aquastand portátil con dispensador de jabón sin contacto',
      'Estación Aquapop de lavado de manos portátil',
      'Detalle del sistema Aquapop mostrando dispensadores automáticos'
    ],
    features: [
      {
        icon: '👋',
        title: 'Sin Contacto',
        description: 'Sensores automáticos para máxima higiene'
      },
      {
        icon: '🚰',
        title: 'Agua Caliente',
        description: 'Sistema de calentamiento instantáneo incluido'
      },
      {
        icon: '🧴',
        title: 'Dispensadores Automáticos',
        description: 'Jabón y desinfectante con sensores'
      },
      {
        icon: '💨',
        title: 'Secado por Aire',
        description: 'Secador de manos de alta velocidad integrado'
      },
      {
        icon: '🔋',
        title: 'Autónomo',
        description: 'Sistema eléctrico independiente con batería'
      },
      {
        icon: '📱',
        title: 'Monitoreo Remoto',
        description: 'Control de niveles y estado via app'
      }
    ],
    hasVariants: true,
    variants: [
      {
        id: 'aquastand',
        name: 'Aquastand',
        description: 'Innovadoras y funcionales, estas unidades sanitarias portátiles reducen el mantenimiento y optimizan la higiene para mayor eficiencia.',
        specifications: [
          { label: 'Color', value: 'Gris' },
          { label: 'Peso Neto', value: '28.57 kilogramos' },
          { label: 'Alto', value: '139.70 cm' },
          { label: 'Largo', value: '81.28 cm' },
          { label: 'Ancho', value: '58.42 cm' },
          { label: 'Capacidad', value: '87 litros' }
        ],
        gallery: [    
          '/images/gallery/galeria lavamanos 2_mgzsuoy73bw1d.webp',
          '/images/gallery/galeria lavamanos 3_mgzsuoyctinhb.webp',
          '/images/gallery/galeria lavamanos 4_mgzsuoyg9a2d7.webp'
        ]
      },
      {
        id: 'aquapop',
        name: 'Aquapop',
        description: 'Unidad sanitaria portátil individual o doble, ligera y fácil de trasladar gracias a su diseño con ruedas. Incluye espacio para dispensador de jabón y toallas de papel.',
        specifications: [
          { label: 'Color', value: 'Gris' },
          { label: 'Peso Neto', value: '20 kilogramos' },
          { label: 'Dimensiones', value: '125cm x 62cm x 42cm' },
          { label: 'Capacidad', value: '50 litros' },
          { label: 'Material', value: 'Polietileno de alta calidad' }
        ],
        gallery: [
          '/images/gallery/galeria lavamanos 1_mgzsuoy4r5caj.webp'
        ]
      }
    ],
    specifications: [],
    gallery: [
      '/images/gallery/galeria lavamanos 1_mgzsuoy4r5caj.webp',
      '/images/gallery/galeria lavamanos 2_mgzsuoy73bw1d.webp',
      '/images/gallery/galeria lavamanos 3_mgzsuoyctinhb.webp',
      '/images/gallery/galeria lavamanos 4_mgzsuoyg9a2d7.webp'
    ],
    pricing: {
      basePrice: '$65.000',
      priceDescription: 'por día',
      additionalInfo: 'Incluye suministros (jabón, desinfectante), mantenimiento y reposición de productos durante el evento.'
    },
    benefits: [
      'Máxima higiene sin contacto físico',
      'Reduce riesgo de contaminación cruzada',
      'Fácil instalación sin conexiones',
      'Monitoreo remoto en tiempo real',
      'Productos de higiene premium incluidos',
      'Cumple normativas sanitarias más estrictas'
    ],
    applications: [
      'Eventos post-pandemia',
      'Centros médicos temporales',
      'Industria alimentaria',
      'Escuelas y colegios',
      'Oficinas corporativas',
      'Centros comerciales'
    ]
  },

  'banos-para-discapacitados': {
    title: 'Baños para Discapacitados',
    subtitle: 'Sanitario accesible con ingreso lateral diseñado para personas en silla de ruedas',
    description: 'Cuenta con pasamanos metálicos para mayor seguridad, asiento con tapa, piso antideslizante, techo inclinado que facilita la ventilación, cierre automático de la puerta, portarrollos triple, gancho para objetos personales, sistema de ventilación optimizada e indicador externo de ocupado o libre.',
    mainImage: '/images/banos-discapacitados-main.jpg',
    mainImageAlt: 'Baño portátil accesible para personas con discapacidad Junisama con rampa de acceso y pasamanos en Colombia',
    bannerImages: [
      '/images/services/Bano-discapacitados.webp',
      '/images/services/Bano-discapacitados-2.webp'
    ],
    bannerImagesAlt: [
      'Vista exterior del baño portátil accesible Junisama con rampa de entrada lateral',
      'Interior espacioso del baño para discapacitados mostrando pasamanos y espacio para silla de ruedas'
    ],
    features: [
      {
        icon: '♿',
        title: 'Ingreso Lateral',
        description: 'Diseño con ingreso lateral para sillas de ruedas'
      },
      {
        icon: '🤝',
        title: 'Pasamanos Metálicos',
        description: 'Pasamanos de seguridad para apoyo y estabilidad'
      },
      {
        icon: '🚽',
        title: 'Asiento con Tapa',
        description: 'Inodoro completo con asiento y tapa'
      },
      {
        icon: '👟',
        title: 'Piso Antideslizante',
        description: 'Superficie segura y antideslizante'
      },
      {
        icon: '🌬️',
        title: 'Ventilación Optimizada',
        description: 'Techo inclinado que facilita la ventilación'
      },
      {
        icon: '🚪',
        title: 'Cierre Automático',
        description: 'Puerta con sistema de cierre automático'
      },
      {
        icon: '🧻',
        title: 'Portarrollos Triple',
        description: 'Sistema triple de portarrollos incluido'
      },
      {
        icon: '🧥',
        title: 'Gancho para Objetos',
        description: 'Gancho para colgar objetos personales'
      },
      {
        icon: '🚦',
        title: 'Indicador de Ocupación',
        description: 'Indicador externo de ocupado o libre'
      }
    ],
    specifications: [
      { label: 'Altura (interna y externa)', value: '2.2 m - 2.23 m' },
      { label: 'Peso neto', value: '110 kilogramos' },
      { label: 'Número de usos', value: '150' },
      { label: 'Capacidad', value: '120 litros' },
      { label: 'Ancho (interno y externo)', value: '1.6 m - 1.65 m' }
    ],
    pricing: {
      basePrice: '$120.000',
      priceDescription: 'por día',
      additionalInfo: 'Incluye instalación especializada, rampa de acceso y mantenimiento. Descuentos por alquiler prolongado.'
    },
    gallery: [
      '/images/gallery/galeria discapacitados 1_mgzsuoykc77pr.webp',
      '/images/gallery/galeria discapacitados 2_mgzsuoyp58ewg.webp'
    ],
    benefits: [
      'Cumple normativas de accesibilidad',
      'Instalación especializada incluida',
      'Personal capacitado en atención',
      'Mantenimiento frecuente garantizado',
      'Señalización apropiada incluida',
      'Certificación de cumplimiento'
    ],
    applications: [
      'Eventos públicos y gubernamentales',
      'Centros médicos y hospitales',
      'Instituciones educativas',
      'Centros comerciales',
      'Eventos deportivos inclusivos',
      'Obras con normativa de accesibilidad'
    ]
  },

  'banos-portatiles-electricos': {
    title: 'Baños Portátiles Eléctricos',
    subtitle: 'Baño eléctrico equipado con inodoro y lavamanos integrados, descarga automática, iluminación LED y sistema de cierre automático',
    description: 'Cuenta con ventilación eficiente y piso con acabado efecto madera. Incluye dispensadores, papelera, espejo, gancho para objetos y conexión para batería de 65 Ah, ofreciendo comodidad y funcionalidad en un diseño moderno y autosuficiente.',
    mainImage: '/images/banos-electricos-main.jpg',
    mainImageAlt: 'Baño portátil eléctrico Junisama con lavamanos integrado, iluminación LED y descarga automática para eventos en Colombia',
    bannerImages: [
      '/images/services/bano-electrico.webp'
    ],
    bannerImagesAlt: [
      'Interior del baño portátil eléctrico Junisama mostrando lavamanos, iluminación LED y acabados premium'
    ],
    features: [
      {
        icon: '🚽',
        title: 'Inodoro Integrado',
        description: 'Inodoro con sistema de descarga automática'
      },
      {
        icon: '🚿',
        title: 'Lavamanos Integrado',
        description: 'Lavamanos con dispensadores incluidos'
      },
      {
        icon: '💡',
        title: 'Iluminación LED',
        description: 'Sistema de iluminación LED eficiente'
      },
      {
        icon: '🚪',
        title: 'Cierre Automático',
        description: 'Sistema de cierre automático de puerta'
      },
      {
        icon: '🌬️',
        title: 'Ventilación Eficiente',
        description: 'Sistema de ventilación optimizada'
      },
      {
        icon: '🪵',
        title: 'Piso Efecto Madera',
        description: 'Acabado de piso con efecto madera'
      },
      {
        icon: '🪞',
        title: 'Espejo y Accesorios',
        description: 'Espejo, dispensadores, papelera y gancho incluidos'
      },
      {
        icon: '🔋',
        title: 'Batería 65 Ah',
        description: 'Conexión para batería de 65 Ah autosuficiente'
      },
      {
        icon: '💧',
        title: 'Bombas de Agua',
        description: 'Sistema de bombeo de agua limpia y aguas grises'
      }
    ],
    specifications: [
      { label: 'Altura (interna y externa)', value: '2.12 m - 2.4 m' },
      { label: 'Peso neto', value: '104 kilogramos' },
      { label: 'Consumo de agua por descarga', value: '1 L' },
      { label: 'Power supply', value: '12 V' },
      { label: 'Ancho (interno y externo)', value: '1.01 m - 1.06 m' },
      { label: 'Bomba de agua limpia', value: 'Desde 10 L/min' },
      { label: 'Bomba de aguas grises', value: 'Desde 18 L/min' }
    ],
    pricing: {
      basePrice: '$95.000',
      priceDescription: 'por día',
      additionalInfo: 'Incluye conexión eléctrica, mantenimiento diario y monitoreo remoto. Tarifas especiales para eventos de larga duración.'
    },
    gallery: [
      '/images/gallery/galeria electrico 1_mgzsuoyqvo6g5.webp',
      '/images/gallery/galeria electrico 2_mgzsuoyuvsg13.webp',
      '/images/gallery/galeria electricos 3_mgzsuoz0w28n5.webp',
      '/images/gallery/galeria electrico 4_mgzsuoy0em771.webp'
    ],
    benefits: [
      'Confort superior con climatización',
      'Iluminación automática inteligente',
      'Ventilación continua garantizada',
      'Monitoreo remoto 24/7',
      'Sistema autónomo con batería',
      'Bajo consumo energético'
    ],
    applications: [
      'Eventos nocturnos y de invierno',
      'Producciones cinematográficas',
      'Campamentos temporales',
      'Obras en zonas rurales',
      'Eventos corporativos premium',
      'Instalaciones temporales de lujo'
    ]
  },

  'trailer-de-lujo': {
    title: 'Tráiler de Lujo',
    subtitle: 'Tráiler de lujo que combina elegancia y confort, brindando una experiencia exclusiva para sus invitados',
    description: 'Cada detalle ha sido diseñado para ofrecer el máximo nivel de comodidad y sofisticación en eventos de alto nivel.',
    mainImage: '/images/trailer-lujo-main.jpg',
    mainImageAlt: 'Tráiler de baños de lujo Junisama con múltiples unidades sanitarias premium para eventos exclusivos en Colombia',
    bannerImages: [
      '/images/services/trailer-de-lujo.webp'
    ],
    bannerImagesAlt: [
      'Vista exterior del tráiler de lujo Junisama con acabados elegantes y diseño sofisticado'
    ],
    features: [
      {
        icon: '🚐',
        title: 'Unidad Móvil',
        description: 'Tráiler completamente autónomo y fácil de transportar'
      },
      {
        icon: '🚿',
        title: 'Múltiples Cabinas',
        description: '4-6 cabinas independientes con ducha y tocador'
      },
      {
        icon: '❄️',
        title: 'Climatización Central',
        description: 'Aire acondicionado y calefacción en todas las cabinas'
      },
      {
        icon: '💎',
        title: 'Acabados Premium',
        description: 'Materiales de lujo, espejos, iluminación designer'
      },
      {
        icon: '🎵',
        title: 'Sistema de Audio',
        description: 'Música ambiental y sistema de comunicación'
      },
      {
        icon: '⚡',
        title: 'Generador Incluido',
        description: 'Sistema eléctrico completamente autónomo'
      }
    ],
    specifications: [],
    gallery: [
      '/images/gallery/galeria trailer de lujo 1_mgzsv4zs0xozy.webp',
      '/images/gallery/galeria trailer de lujo 2_mgzsv4zua0lmu.webp',
      '/images/gallery/galeria trailer de lujo 3_mgzsv4zw7ln2o.webp',
      '/images/gallery/galeria trailer de lujo 4_mgzsv4zylm9ul.webp'
    ],
    pricing: {
      basePrice: '$850.000',
      priceDescription: 'por día',
      additionalInfo: 'Incluye transporte, instalación, operador, combustible y mantenimiento completo. Descuentos para eventos de múltiples días.'
    },
    benefits: [
      'Máximo lujo y confort para invitados VIP',
      'Completamente autónomo',
      'Personal operador incluido',
      'Transporte e instalación incluidos',
      'Múltiples cabinas independientes',
      'Ideal para eventos exclusivos'
    ],
    applications: [
      'Bodas de alta gama',
      'Eventos corporativos ejecutivos',
      'Producciones de TV y cine',
      'Festivales VIP',
      'Eventos gubernamentales',
      'Lanzamientos de productos premium'
    ]
  },

  'servicio-de-operarios': {
    title: 'Servicio de Operarios',
    subtitle: 'Personal especializado para mantenimiento y atención',
    description: 'Servicio profesional de operarios especializados en mantenimiento de instalaciones sanitarias temporales, garantizando limpieza, funcionamiento óptimo y atención al usuario durante todo el evento.',
    mainImage: '/images/operarios-main.jpg',
    mainImageAlt: 'Operarios certificados de Junisama realizando mantenimiento profesional de baños portátiles en eventos en Colombia',
    bannerImages: [
      '/images/services/operarios.webp'
    ],
    bannerImagesAlt: [
      'Personal técnico especializado de Junisama en uniforme realizando servicio de limpieza y mantenimiento'
    ],
    features: [
      {
        icon: '👷',
        title: 'Personal Capacitado',
        description: 'Operarios certificados y uniformados profesionalmente'
      },
      {
        icon: '🧽',
        title: 'Limpieza Constante',
        description: 'Mantenimiento cada 2-4 horas según demanda'
      },
      {
        icon: '🔧',
        title: 'Mantenimiento Técnico',
        description: 'Reparaciones menores y ajustes inmediatos'
      },
      {
        icon: '📦',
        title: 'Reposición de Suministros',
        description: 'Papel, jabón, toallas y productos de higiene'
      },
      {
        icon: '📱',
        title: 'Comunicación Directa',
        description: 'Línea directa con coordinador del evento'
      },
      {
        icon: '⏰',
        title: 'Horarios Flexibles',
        description: 'Turnos de 8, 12 o 24 horas según necesidad'
      }
    ],
    specifications: [
      { label: 'Turnos disponibles', value: '8, 12 o 24 horas' },
      { label: 'Personal por turno', value: '1-3 operarios según escala' },
      { label: 'Capacidad atención', value: 'Hasta 50 unidades' },
      { label: 'Tiempo respuesta', value: 'Máximo 15 minutos' },
      { label: 'Certificaciones', value: 'Manejo de residuos, SISO' },
      { label: 'Uniformes', value: 'Incluidos y marcados' },
      { label: 'Herramientas', value: 'Kit completo incluido' },
      { label: 'Comunicación', value: 'Radio y celular' }
    ],
    pricing: {
      basePrice: '$180.000',
      priceDescription: 'por turno de 8 horas',
      additionalInfo: 'Incluye operario, uniformes, herramientas y suministros básicos. Tarifas especiales para eventos de múltiples días.'
    },
    benefits: [
      'Garantiza higiene constante',
      'Atención profesional a usuarios',
      'Reposición oportuna de suministros',
      'Solución inmediata de problemas',
      'Personal uniformado y capacitado',
      'Comunicación directa con organizadores'
    ],
    applications: [
      'Eventos masivos y festivales',
      'Obras de construcción',
      'Eventos corporativos importantes',
      'Ferias comerciales',
      'Eventos deportivos',
      'Cualquier evento de larga duración'
    ]
  },

  'puntos-ecologicos': {
    title: 'Puntos Ecológicos',
    subtitle: 'Soluciones sostenibles para gestión de residuos',
    description: 'Estaciones ecológicas portátiles para separación y manejo responsable de residuos, contribuyendo a la sostenibilidad ambiental de tu evento con diseño moderno y funcional.',
    mainImage: '/images/puntos-ecologicos-main.jpg',
    mainImageAlt: 'Punto ecológico portátil Junisama con separación de residuos reciclables para eventos sostenibles en Colombia',
    bannerImages: [
      '/images/services/punto-ecologico.webp'
    ],
    bannerImagesAlt: [
      'Estación de reciclaje ecológica Junisama con compartimientos codificados por color para separación de residuos'
    ],
    features: [
      {
        icon: '♻️',
        title: 'Separación Inteligente',
        description: 'Sistema de 4 compartimentos claramente identificados'
      },
      {
        icon: '🌱',
        title: 'Materiales Eco-Friendly',
        description: 'Construidos con materiales reciclados y reciclables'
      },
      {
        icon: '🏷️',
        title: 'Señalización Clara',
        description: 'Pictogramas universales y código de colores'
      },
      {
        icon: '🔒',
        title: 'Sistema Anti-Derrame',
        description: 'Diseño que previene derrames y olores'
      },
      {
        icon: '📊',
        title: 'Reporte de Gestión',
        description: 'Informe de residuos recolectados y reciclados'
      },
      {
        icon: '🚛',
        title: 'Recolección Especializada',
        description: 'Servicio de recolección diferenciada incluido'
      }
    ],
    specifications: [
      { label: 'Compartimentos', value: '4 tipos de residuos' },
      { label: 'Capacidad total', value: '240 litros' },
      { label: 'Material', value: 'Polietileno reciclado' },
      { label: 'Dimensiones', value: '1.5m x 0.8m x 1.2m' },
      { label: 'Peso', value: '45 kg (vacío)' },
      { label: 'Resistencia', value: 'Intemperie UV' },
      { label: 'Señalización', value: 'Vinil reflectivo' },
      { label: 'Certificación', value: 'ISO 14001' }
    ],
    pricing: {
      basePrice: '$35.000',
      priceDescription: 'por día',
      additionalInfo: 'Incluye instalación, recolección diferenciada y reporte de gestión ambiental. Descuentos por volumen.'
    },
    benefits: [
      'Contribuye a la sostenibilidad del evento',
      'Mejora la imagen ambiental corporativa',
      'Cumple normativas ambientales',
      'Educación ambiental para asistentes',
      'Reporte de impacto incluido',
      'Certificación de gestión responsable'
    ],
    applications: [
      'Eventos con certificación ambiental',
      'Festivales eco-friendly',
      'Eventos corporativos sostenibles',
      'Ferias ambientales',
      'Instituciones educativas',
      'Eventos gubernamentales'
    ]
  }
};

export const getServiceData = (serviceSlug: string): ServiceData | null => {
  return servicesData[serviceSlug] || null;
};